/**
 * Syllabus Upload Library
 * Handles file upload to GCS and triggers AI parsing
 *
 * Uses two-phase GCS upload flow (same as Outline IQ):
 * 1. Get signed URL from backend
 * 2. Upload file directly to GCS (no timeout issues)
 * 3. Trigger parsing via Edge Function
 */

import { AI_BASE_URL } from '@/lib/ai-base-url';
import { getAuthTokenOrThrow } from '@/lib/auth-token';
import { logger } from '@/lib/logger';
import type {
  SyllabusUploadUrlResponse,
  SyllabusBatchUploadUrlResponse,
  SyllabusParsingEvent,
  ParsedSyllabusData,
} from '@/types/syllabus';
import {
  MAX_SYLLABUS_FILE_SIZE,
  ALLOWED_SYLLABUS_MIME_TYPES,
  FILE_TYPE_FROM_EXTENSION,
} from '@/types/syllabus';

// Multi-file upload constraints
const MAX_MULTI_FILE_COUNT = 10;
const MAX_MULTI_FILE_TOTAL_SIZE = 100 * 1024 * 1024; // 100MB total
const IMAGE_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/heic',
] as const;

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * Get the Syllabus Parser MIG service URL
 */
function getSyllabusStreamUrl(): string {
  return `${AI_BASE_URL}/syllabus`;
}

// ============================================================================
// TYPES
// ============================================================================

export type UploadPhase = 'validating' | 'preparing' | 'uploading' | 'parsing' | 'complete';

export interface UploadProgress {
  phase: UploadPhase;
  progress: number; // 0-100
  message?: string;
}

export type UploadProgressCallback = (progress: UploadProgress) => void;

export interface SyllabusUploadResult {
  success: boolean;
  syllabusId?: string;
  parsedData?: ParsedSyllabusData;
  error?: string;
}

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Validate a file before upload
 */
export function validateSyllabusFile(file: File): { valid: boolean; error?: string } {
  // Check file size
  if (file.size > MAX_SYLLABUS_FILE_SIZE) {
    return {
      valid: false,
      error: `File too large. Maximum size is ${MAX_SYLLABUS_FILE_SIZE / (1024 * 1024)}MB.`,
    };
  }

  // Check MIME type
  const allowedTypes = ALLOWED_SYLLABUS_MIME_TYPES as readonly string[];
  if (!allowedTypes.includes(file.type)) {
    // Also check extension as fallback
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !FILE_TYPE_FROM_EXTENSION[extension]) {
      return {
        valid: false,
        error: 'Invalid file type. Only PDF, DOC, DOCX, TXT, and image files are supported.',
      };
    }
  }

  return { valid: true };
}

/**
 * Get file type from file object
 */
export function getFileType(file: File): string {
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  return FILE_TYPE_FROM_EXTENSION[extension] || 'pdf';
}

// ============================================================================
// UPLOAD FUNCTIONS
// ============================================================================

/**
 * Upload a syllabus file and trigger parsing
 *
 * @param file - The syllabus file to upload
 * @param courseId - Optional course ID to link syllabus to
 * @param onProgress - Progress callback
 * @returns Upload result with syllabus ID and parsed data
 */
export async function uploadSyllabus(
  file: File,
  courseId?: string,
  onProgress?: UploadProgressCallback
): Promise<SyllabusUploadResult> {
  try {
    // Phase 1: Validate file
    onProgress?.({ phase: 'validating', progress: 0, message: 'Validating file...' });

    const validation = validateSyllabusFile(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    onProgress?.({ phase: 'validating', progress: 100, message: 'File validated' });

    // Phase 2: Get signed upload URL
    onProgress?.({ phase: 'preparing', progress: 0, message: 'Preparing upload...' });

    const token = await getAuthTokenOrThrow();

    const uploadUrlResponse = await getUploadUrl(file, courseId, token);
    onProgress?.({ phase: 'preparing', progress: 100, message: 'Upload prepared' });

    // Phase 3: Upload file directly to GCS
    onProgress?.({ phase: 'uploading', progress: 0, message: 'Uploading file...' });

    await uploadToGCS(uploadUrlResponse.uploadUrl, file, (percent) => {
      onProgress?.({
        phase: 'uploading',
        progress: percent,
        message: `Uploading... ${percent}%`,
      });
    });

    // Phase 4: Start parsing
    onProgress?.({ phase: 'parsing', progress: 0, message: 'Analyzing syllabus...' });

    const result = await startParsing(
      uploadUrlResponse.uploadId,
      uploadUrlResponse.gcsPath,
      undefined,
      courseId,
      token,
      (event) => {
        if (event.type === 'progress' || event.type === 'stage') {
          const stageProgress: Record<string, number> = {
            extracting: 25,
            parsing: 50,
            mapping: 75,
            saving: 90,
          };
          const progress = event.stage ? stageProgress[event.stage] || 50 : event.progress || 50;
          onProgress?.({
            phase: 'parsing',
            progress,
            message: event.message || `${event.stage}...`,
          });
        }
      }
    );

    onProgress?.({ phase: 'complete', progress: 100, message: 'Complete!' });

    return {
      success: true,
      syllabusId: uploadUrlResponse.uploadId,
      parsedData: result,
    };
  } catch (error) {
    logger.error('Error uploading syllabus:', error);

    // Enhance error messages
    if (error instanceof Error) {
      const message = error.message.toLowerCase();

      if (message.includes('401') || message.includes('unauthorized')) {
        throw new Error('Authentication failed. Please sign out and sign back in.');
      }

      if (message.includes('500') || message.includes('502') || message.includes('503')) {
        throw new Error('Service temporarily unavailable. Please try again in a few minutes.');
      }

      if (message.includes('network') || message.includes('failed to fetch')) {
        throw new Error('Network error. Please check your connection and try again.');
      }
    }

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to upload syllabus',
    };
  }
}

/**
 * Validate files for batch upload (multi-image)
 */
export function validateBatchFiles(files: File[]): { valid: boolean; error?: string } {
  if (files.length === 0) {
    return { valid: false, error: 'No files selected' };
  }

  if (files.length > MAX_MULTI_FILE_COUNT) {
    return { valid: false, error: `Maximum ${MAX_MULTI_FILE_COUNT} files allowed` };
  }

  // Check total size
  const totalSize = files.reduce((sum, f) => sum + f.size, 0);
  if (totalSize > MAX_MULTI_FILE_TOTAL_SIZE) {
    return {
      valid: false,
      error: `Total file size exceeds ${MAX_MULTI_FILE_TOTAL_SIZE / (1024 * 1024)}MB limit`,
    };
  }

  // Check that all files are images
  const imageMimeTypes = IMAGE_MIME_TYPES as readonly string[];
  for (const file of files) {
    if (!imageMimeTypes.includes(file.type)) {
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (!ext || !['png', 'jpg', 'jpeg', 'webp', 'heic'].includes(ext)) {
        return {
          valid: false,
          error: `Multi-file upload only supports images. "${file.name}" is not supported.`,
        };
      }
    }
  }

  return { valid: true };
}

/**
 * Upload multiple syllabus images and trigger parsing
 *
 * @param files - Array of image files to upload
 * @param courseId - Optional course ID to link syllabus to
 * @param onProgress - Progress callback
 * @returns Upload result with syllabus ID and parsed data
 */
export async function uploadMultipleSyllabusImages(
  files: File[],
  courseId?: string,
  onProgress?: UploadProgressCallback
): Promise<SyllabusUploadResult> {
  try {
    // Phase 1: Validate files
    onProgress?.({ phase: 'validating', progress: 0, message: 'Validating files...' });

    const validation = validateBatchFiles(files);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    onProgress?.({ phase: 'validating', progress: 100, message: 'Files validated' });

    // Phase 2: Get batch signed upload URLs
    onProgress?.({ phase: 'preparing', progress: 0, message: 'Preparing upload...' });

    const token = await getAuthTokenOrThrow();

    const batchResponse = await getBatchUploadUrls(files, courseId, token);
    onProgress?.({ phase: 'preparing', progress: 100, message: 'Upload prepared' });

    // Phase 3: Upload files directly to GCS (in parallel)
    onProgress?.({ phase: 'uploading', progress: 0, message: 'Uploading files...' });

    const uploadPromises = batchResponse.uploadUrls.map((urlInfo, index) =>
      uploadToGCS(urlInfo.uploadUrl, files[index], () => {
        // Individual file progress - aggregate to overall progress
        const overallProgress = Math.round(((index + 1) / files.length) * 100);
        onProgress?.({
          phase: 'uploading',
          progress: overallProgress,
          message: `Uploading ${index + 1}/${files.length}...`,
        });
      })
    );

    await Promise.all(uploadPromises);

    // Phase 4: Start parsing
    onProgress?.({ phase: 'parsing', progress: 0, message: 'Analyzing syllabus...' });

    const batchGcsPaths = batchResponse.uploadUrls.map((u) => u.gcsPath);
    const result = await startParsing(
      batchResponse.uploadId,
      batchGcsPaths[0],
      batchGcsPaths,
      courseId,
      token,
      (event) => {
        if (event.type === 'progress' || event.type === 'stage') {
          const stageProgress: Record<string, number> = {
            extracting: 25,
            parsing: 50,
            mapping: 75,
            saving: 90,
          };
          const progress = event.stage ? stageProgress[event.stage] || 50 : event.progress || 50;
          onProgress?.({
            phase: 'parsing',
            progress,
            message: event.message || `${event.stage}...`,
          });
        }
      }
    );

    onProgress?.({ phase: 'complete', progress: 100, message: 'Complete!' });

    return {
      success: true,
      syllabusId: batchResponse.uploadId,
      parsedData: result,
    };
  } catch (error) {
    logger.error('Error uploading multiple syllabus images:', error);

    // Enhance error messages
    if (error instanceof Error) {
      const message = error.message.toLowerCase();

      if (message.includes('401') || message.includes('unauthorized')) {
        throw new Error('Authentication failed. Please sign out and sign back in.');
      }

      if (message.includes('500') || message.includes('502') || message.includes('503')) {
        throw new Error('Service temporarily unavailable. Please try again in a few minutes.');
      }

      if (message.includes('network') || message.includes('failed to fetch')) {
        throw new Error('Network error. Please check your connection and try again.');
      }
    }

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to upload syllabus images',
    };
  }
}

/**
 * Get batch signed upload URLs from backend
 */
async function getBatchUploadUrls(
  files: File[],
  courseId: string | undefined,
  token: string
): Promise<SyllabusBatchUploadUrlResponse> {
  const response = await fetch(`${getSyllabusStreamUrl()}/batch-generate-upload-urls`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      files: files.map((f) => ({
        fileName: f.name,
        fileType: f.type || 'application/octet-stream',
        fileSizeBytes: f.size,
      })),
      courseId,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to get batch upload URLs (${response.status})`);
  }

  return response.json();
}

/**
 * Get signed upload URL from backend
 */
async function getUploadUrl(
  file: File,
  courseId: string | undefined,
  token: string
): Promise<SyllabusUploadUrlResponse> {
  const response = await fetch(`${getSyllabusStreamUrl()}/generate-upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      fileName: file.name,
      fileType: file.type || 'application/octet-stream',
      fileSizeBytes: file.size,
      courseId,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to get upload URL (${response.status})`);
  }

  return response.json();
}

/**
 * Upload file directly to GCS using signed URL
 */
function uploadToGCS(
  uploadUrl: string,
  file: File,
  onProgress: (percent: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Track upload progress
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      }
    });

    // Handle successful response
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed (${xhr.status}): ${xhr.responseText}`));
      }
    });

    // Handle network errors
    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload. Please check your connection.'));
    });

    // Handle timeout
    xhr.addEventListener('timeout', () => {
      reject(new Error('Upload timed out. Please try again.'));
    });

    // GCS signed URL expects PUT with raw file data
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.timeout = 600000; // 10 minute timeout for large files
    xhr.send(file);
  });
}

/**
 * Start parsing after file uploaded to GCS
 * Uses SSE for real-time progress updates
 */
async function startParsing(
  uploadId: string,
  gcsPath: string,
  gcsPathsList: string[] | undefined,
  courseId: string | undefined,
  token: string,
  onEvent?: (event: SyllabusParsingEvent) => void
): Promise<ParsedSyllabusData | undefined> {
  return new Promise((resolve, reject) => {
    // Build request body matching MIG's ParseSyllabusRequest schema
    const requestBody: Record<string, unknown> = {
      syllabus_id: uploadId,
      course_id: courseId,
    };
    if (gcsPathsList && gcsPathsList.length > 1) {
      requestBody.gcs_paths = gcsPathsList.map((p) => `gs://jds-syllabi/${p}`);
    } else {
      requestBody.gcs_path = `gs://jds-syllabi/${gcsPath}`;
    }

    // Call MIG parse-syllabus directly
    fetch(`${getSyllabusStreamUrl()}/parse-syllabus`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
      },
      body: JSON.stringify(requestBody),
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Failed to start parsing (${response.status})`);
        }

        // Check if response is SSE
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('text/event-stream')) {
          // Handle SSE stream
          const reader = response.body?.getReader();
          if (!reader) {
            throw new Error('Failed to read response stream');
          }

          const decoder = new TextDecoder();
          let buffer = '';
          let parsedData: ParsedSyllabusData | undefined;

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const event = JSON.parse(line.slice(6)) as SyllabusParsingEvent;
                  onEvent?.(event);

                  if (event.type === 'result' && event.data) {
                    parsedData = event.data;
                  }

                  if (event.type === 'complete') {
                    resolve(parsedData);
                    return;
                  }

                  if (event.type === 'error') {
                    reject(new Error(event.error || 'Parsing failed'));
                    return;
                  }
                } catch {
                  // Ignore malformed events
                }
              }
            }
          }

          resolve(parsedData);
        } else {
          // Handle regular JSON response
          const data = await response.json();
          if (data.success && data.parsedData) {
            resolve(data.parsedData);
          } else if (data.error) {
            reject(new Error(data.error));
          } else {
            resolve(undefined);
          }
        }
      })
      .catch(reject);
  });
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Get file icon based on type
 */
export function getFileIcon(fileName: string): 'pdf' | 'docx' | 'doc' | 'txt' | 'image' {
  const extension = fileName.split('.').pop()?.toLowerCase() || '';
  return FILE_TYPE_FROM_EXTENSION[extension] || 'pdf';
}
